// physics.js — 玉の運動と衝突解決（純粋モジュール）

import { GRAVITY, BALL_R, DRAIN_Y, RAIL_X, LAUNCH_POS, powerToSpeed } from './board.js';

let uid = 0;

export function launchBall(power, jitter = 0) {
  const v = powerToSpeed(power) * (1 + (jitter - 0.5) * 0.014);
  return {
    id: ++uid,
    x: LAUNCH_POS.x, y: LAUNCH_POS.y,
    vx: 0, vy: -v,
    r: BALL_R,
    inRail: true,
    age: 0,
    spin: 0,
    trail: [],
    alive: true,
  };
}

function bounceOffPoint(b, cx, cy, R, rest, fric, chaos) {
  let dx = b.x - cx, dy = b.y - cy;
  let d = Math.hypot(dx, dy);
  const min = b.r + R;
  if (d >= min) return 0;
  if (d < 1e-6) { dx = 0; dy = -1; d = 1; }
  const nx = dx / d, ny = dy / d;

  b.x += nx * (min - d);
  b.y += ny * (min - d);

  const vn = b.vx * nx + b.vy * ny;
  if (vn >= 0) return 0;

  const tx = -ny, ty = nx;
  let vt = b.vx * tx + b.vy * ty;
  vt *= fric;
  if (chaos) vt += (Math.random() - 0.5) * chaos;

  const vnNew = -vn * rest;
  b.vx = nx * vnNew + tx * vt;
  b.vy = ny * vnNew + ty * vt;
  b.spin += vt * 0.08;
  return -vn;
}

function bounceOffSegment(b, s) {
  const ax = s.x1, ay = s.y1;
  const bx = s.x2 - ax, by = s.y2 - ay;
  const len2 = bx * bx + by * by;
  let t = len2 > 0 ? ((b.x - ax) * bx + (b.y - ay) * by) / len2 : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return bounceOffPoint(b, ax + bx * t, ay + by * t, s.thick, s.rest, s.fric, 0);
}

function windmillSegments(wm) {
  const segs = [];
  for (let i = 0; i < wm.blades; i++) {
    const a = wm.phase + (i * Math.PI * 2) / wm.blades;
    segs.push({
      x1: wm.x + Math.cos(a) * 4, y1: wm.y + Math.sin(a) * 4,
      x2: wm.x + Math.cos(a) * wm.r, y2: wm.y + Math.sin(a) * wm.r,
      thick: 3.2, rest: 0.30, fric: 0.86,
    });
  }
  return segs;
}

const SUBSTEPS = 4;
const BALL_REST = 0.42;      // 玉同士の反発係数

// 玉1個をサブステップ1つ分だけ進め、盤面（壁・釘・風車）と衝突させる
function advance(b, board, wmSegs, h, events) {
  b.vy += GRAVITY * h;
  b.vx *= 0.99955;
  b.x += b.vx * h;
  b.y += b.vy * h;

  // レール内判定（釘との衝突を無視するため）
  const inRail = b.x > RAIL_X - b.r * 0.5;
  b.inRail = inRail;

  // 壁・パネル
  for (const seg of board.segments) {
    if (seg.id === 'gate' && board.gateOpen) continue;
    const imp = bounceOffSegment(b, seg);
    if (imp > 1.6) events.push({ type: 'wall', kind: seg.kind, x: b.x, y: b.y, power: imp });
  }

  if (inRail) return;

  // 釘
  for (const p of board.pegs) {
    if (Math.abs(p.x - b.x) > 16 || Math.abs(p.y - b.y) > 16) continue;
    const imp = bounceOffPoint(b, p.x, p.y, p.r, 0.52, 0.90, 0.55);
    if (imp > 0) {
      p.flash = 1;
      events.push({ type: 'peg', x: p.x, y: p.y, power: imp, peg: p });
    }
  }

  // 風車
  for (let w = 0; w < board.windmills.length; w++) {
    const wm = board.windmills[w];
    if (Math.abs(wm.x - b.x) > wm.r + 14 || Math.abs(wm.y - b.y) > wm.r + 14) continue;
    for (const seg of wmSegs[w]) {
      const imp = bounceOffSegment(b, seg);
      if (imp > 0) {
        const rx = b.x - wm.x, ry = b.y - wm.y;
        const omega = wm.speed * wm.dir;
        b.vx += -ry * omega * 0.85;
        b.vy += rx * omega * 0.85;
        events.push({ type: 'windmill', x: b.x, y: b.y, power: imp, wm });
      }
    }
  }
}

/**
 * 玉同士の衝突。x 昇順に並べ、直径以上離れたら以降は見ない（掃引法）。
 *
 * 発射レール内の玉は対象外にしている。レール幅 30px に対して玉は直径 13px
 * なので 2 個が横に並べてしまい、壁が内側へ・玉同士が外側へ押し合って
 * 釣り合ったまま抜けなくなる。レールは一本道で玉同士の見た目の重なりも
 * 起きないため、ここだけはすり抜けさせる。
 */
function resolveBallCollisions(active, events) {
  active.sort((a, b) => a.x - b.x);

  for (let i = 0; i < active.length; i++) {
    const a = active[i];
    if (!a.alive || a.inRail) continue;

    for (let j = i + 1; j < active.length; j++) {
      const b = active[j];
      const dx = b.x - a.x;
      const minD = a.r + b.r;
      if (dx > minD) break;                     // 以降の玉はもっと右にいる
      if (!b.alive || b.inRail) continue;

      const dy = b.y - a.y;
      if (dy > minD || dy < -minD) continue;

      const d2 = dx * dx + dy * dy;
      if (d2 >= minD * minD || d2 < 1e-9) continue;

      const d = Math.sqrt(d2);
      const nx = dx / d, ny = dy / d;

      // 重なりを等分に押し戻す（質量は同じ）
      const push = (minD - d) * 0.5;
      a.x -= nx * push; a.y -= ny * push;
      b.x += nx * push; b.y += ny * push;

      // 法線方向の相対速度だけ交換する
      const rvn = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny;
      if (rvn >= 0) continue;                   // すでに離れつつある
      const imp = -(1 + BALL_REST) * rvn * 0.5;
      a.vx -= imp * nx; a.vy -= imp * ny;
      b.vx += imp * nx; b.vy += imp * ny;

      if (-rvn > 1.4) {
        events.push({ type: 'ball', x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, power: -rvn });
      }
    }
  }
}

/**
 * 1フレーム分ワールドを進める。
 * events に {type, ...} を push して返す
 * （type: peg / wall / windmill / ball / pocket / drain / rail-return）
 */
export function stepWorld(board, balls, events) {
  for (const wm of board.windmills) wm.phase += wm.speed * wm.dir;

  const wmSegs = board.windmills.map(windmillSegments);
  const h = 1 / SUBSTEPS;

  const active = [];
  for (const b of balls) {
    if (!b.alive) continue;
    b.age++;
    active.push(b);
  }

  // 玉同士をぶつけるには全部の玉が同じ時刻にいる必要があるので、
  // サブステップを外側・玉を内側にして回す
  for (let s = 0; s < SUBSTEPS; s++) {
    for (const b of active) advance(b, board, wmSegs, h, events);
    resolveBallCollisions(active, events);
  }

  for (const b of active) {
    // 速度上限
    const sp = Math.hypot(b.vx, b.vy);
    if (sp > 34) { b.vx = b.vx / sp * 34; b.vy = b.vy / sp * 34; }

    b.trail.push(b.x, b.y);
    if (b.trail.length > 20) b.trail.splice(0, 2);

    // 入賞判定
    if (!b.inRail) {
      for (const pk of board.pockets) {
        if (pk.id === 'attacker' && !board.gateOpen) continue;
        if (b.x > pk.x1 && b.x < pk.x2 && b.y > pk.y1 && b.y < pk.y2) {
          b.alive = false;
          events.push({ type: 'pocket', pocket: pk, x: b.x, y: b.y });
          break;
        }
      }
    }
    if (!b.alive) continue;

    // 発射ミス（レールを登り切れず戻ってきた）
    if (b.inRail && b.vy > 0 && b.y > 755 && b.age > 20) {
      b.alive = false;
      events.push({ type: 'rail-return', x: b.x, y: b.y });
      continue;
    }

    // アウト
    if (b.y > DRAIN_Y) {
      b.alive = false;
      events.push({ type: 'drain', x: b.x, y: b.y });
      continue;
    }

    // 保険（詰まり検知）
    if (b.age > 3600) { b.alive = false; events.push({ type: 'drain', x: b.x, y: b.y }); }
  }

  for (const p of board.pegs) if (p.flash > 0) p.flash -= 0.08;

  return events;
}
